import classNames from 'classnames';
import { useEffect, useState } from 'react';
import EcoLink from '../eco-link';
import './index.scss';

interface EcoPaginationItemProps {
    /**
     * 跳转链接
     */
    href?: string;
    /**
     * 当前页数
     */
    page: number;
    /**
     * 是否激活状态
     */
    active?: boolean;
    /**
     * 是否不可点击
     */
    disabled?: boolean;
}
export interface EcoPaginationProps {
    /**
     * 默认的当前页数
     */
    defaultCurrent?: number;
    /**
     * 当前页数
     */
    current?: number;
    /**
     * 默认的每页条数
     */
    defaultPageSize?: number;
    /**
     * 数据总数
     */
    total?: number;
    /**
     * 是否启动分页中带跳转链接
     */
    isOpenLink?: boolean;
    /**
     * link 的前置链接
     */
    linkBefore?: string;
    /**
     * link 的后置链接
     */
    linkAfter?: string;
    /**
     * 数据总数
     */
    onChange?: (page: number, pageSize: number) => void;
    /**
     * 样式类
     */
    className?: string;
}

/**
 * 分页
 */
export default function EcoPagination({
    defaultCurrent = 1,
    current,
    defaultPageSize = 20,
    total = 0,
    isOpenLink,
    linkBefore,
    linkAfter,
    onChange,
    className,
}: EcoPaginationProps) {
    const prefixCls = 'eco-pagination';
    const prefixItemCls = 'eco-pagination-item';
    const compCls = classNames(prefixCls, {
        [`${className}`]: className,
    });
    const [pageItems, setPageItems] = useState(getPaginationItems(total, current || defaultCurrent, defaultPageSize));

    const [middleStart, setMiddleStart] = useState(2);

    const totalPages = pageItems.length;

    const setMiddleStartByPage = (page: number) => {
        if (totalPages <= 7) return;

        if (page <= 3) {
            setMiddleStart(2);
        } else if (page >= totalPages - 2) {
            setMiddleStart(totalPages - 5);
        } else {
            setMiddleStart(page - 2);
        }
    };

    useEffect(() => {
        setPageItems(getPaginationItems(total, current || defaultCurrent, defaultPageSize));
        setMiddleStartByPage(current || defaultCurrent);
    }, [current, total]);

    if (total <= defaultPageSize) {
        return null;
    }

    const handleClick = (page: any) => {
        if (page < 1 || page > totalPages) return;
        setMiddleStartByPage(page);
        onChange?.(page, defaultPageSize);
    };

    // 点击分支
    const pageClickHandle = (item: EcoPaginationItemProps) => {
        if (onChange) {
            onChange(item.page, defaultPageSize);
        }
    };

    const handleLeftDotsClick = () => {
        const newStart = Math.max(middleStart - 5, 2);
        setMiddleStart(newStart);

        // 仅当不是第一段时，修改当前页为中间页
        if (newStart > 2) {
            const midPage = Math.floor((newStart + Math.min(newStart + 4, totalPages - 1)) / 2);
            onChange?.(midPage, defaultPageSize);
        } else {
            onChange?.(1, defaultPageSize);
        }
    };

    const handleRightDotsClick = () => {
        const newStart = Math.min(middleStart + 5, totalPages - 5);
        setMiddleStart(newStart);

        // 仅当不是最后一段时，修改当前页为中间页
        if (newStart + 4 < totalPages - 1) {
            const midPage = Math.floor((newStart + Math.min(newStart + 4, totalPages - 1)) / 2);
            onChange?.(midPage, defaultPageSize);
        } else {
            onChange?.(totalPages, defaultPageSize);
        }
    };

    const renderPageNumbers = () => {
        const pages: any[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push({ page: i, active: i === current });
        } else {
            pages.push({ page: 1, active: current === 1 });

            const windowSize = 5;
            const left = middleStart;
            const right = Math.min(middleStart + windowSize - 1, totalPages - 1);

            if (left > 2) {
                pages.push('left-dots');
            }

            for (let i = left; i <= right; i++) {
                if (i > 1 && i < totalPages) {
                    pages.push({ page: i, active: i === current });
                }
            }

            if (right < totalPages - 1) {
                pages.push('right-dots');
            }

            pages.push({ page: totalPages, active: current === totalPages });
        }

        return pages.map((item, index) => {
            if (item === 'left-dots') {
                return (
                    <span key={index} onClick={handleLeftDotsClick} className={`${prefixItemCls}-dots`}>
                        •••
                    </span>
                );
            }

            if (item === 'right-dots') {
                return (
                    <span key={index} onClick={handleRightDotsClick} className={`${prefixItemCls}-dots`}>
                        •••
                    </span>
                );
            }

            const page = item.page;
            const itemCls = classNames(prefixItemCls, {
                [`${prefixItemCls}-active`]: item.active,
            });
            const itemLink = `${linkBefore || ''}${page}${linkAfter || ''}`;

            return (
                <li
                    key={index}
                    className={itemCls}
                    onClick={() => {
                        if (!isOpenLink) {
                            setMiddleStartByPage(page);
                            pageClickHandle(item);
                        }
                    }}
                >
                    {isOpenLink && <EcoLink className={`${prefixCls}-link`} href={itemLink}>{page}</EcoLink>}
                    {!isOpenLink && <>{page}</>}
                </li>
            );
        });
    };

    return (
        <ul className={compCls}>
            <li className={`${prefixItemCls}-prev ${current === 1 ? `${prefixItemCls}-disabled` : ''}`} onClick={() => handleClick(current - 1)} />
            {renderPageNumbers()}
            <li className={`${prefixItemCls}-next ${current === totalPages ? `${prefixItemCls}-disabled` : ''}`} onClick={() => handleClick(current + 1)} />
        </ul>
    );
}

const getPaginationItems = (total: number, pageIndex: number, pageSize: number) => {
    const pageIndex2 = Number(pageIndex) || 1;
    const result: EcoPaginationItemProps[] = [];
    const pageMax = Math.ceil(total / pageSize); //最多有多少个分页

    for (let i = 0; i < pageMax; i++) {
        const pageItem = i + 1;
        result.push({
            page: pageItem,
            active: pageItem === pageIndex2,
        });
    }

    return result;
};
